<template>
    <div class="vrm--map__geofence">
        <vrm-mapbox :location="initialCoordinates" @load="onMapboxLoaded"></vrm-mapbox>
    </div>
</template>
<script>
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import { AngularDirective } from '@/vue/decorators/angular-interop';
import VrmMapbox from '@/vue/components/map/VrmMapbox.vue';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import DrawRectangle from '@/vue/components/map/draw/draw_rectangle';
import DrawCircle from '@/vue/components/map/draw/draw_circle';
import CustomDrawControls from '@/vue/components/map/controls/draw_controls';
import {
    parseOldGeofenceShape,
    convertToOldGeofenceShape,
} from '@/vue/components/map/utils/geofence';

import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { drawStyles, mapLayerIndividualInstallation } from '@/vue/components/map/constants';

@Component({
    components: { VrmMapbox },
    emits: ['geofence-changed'],
})
@AngularDirective('vrm-map-geofence-settings')
@AngularLocaleBundle('core')
export default class VrmMapGeofenceSettings extends Vue {
    /**
     * Coordinates of the installation marker.
     */
    @Prop()
    coords;

    /**
     * Geofence data if it exists.
     */
    @Prop()
    geofence;

    /**
     * Internally used geofence data copy.
     *
     * Used to disconnect the map shape from the component prop and prevent unwanted map updates when data changes.
     *
     * @type {null}
     */
    existingGeofence = null;

    initialCoordinates = {
        lat: 0,
        lng: 0,
        zoom: 10,
        bearing: 0,
        pitch: 0,
    };

    temporaryFeatures = [];

    deleted = false;

    created() {
        this.initialCoordinates = {
            lat: this.coords ? this.coords.latitude : 0,
            lng: this.coords ? this.coords.longitude : 0,
            zoom: 10,
            bearing: 0,
            pitch: 0,
        };

        if (this.geofence) {
            const shape = parseOldGeofenceShape(this.geofence);

            if (shape) {
                this.existingGeofence = shape;

                if (this.draw) {
                    this.draw.add(shape);

                    this.initialized = true;
                }
            }
        }
    }

    @Watch('coords')
    onCoordsChanged(value) {
        const zoom = this.map && this.map.getZoom() ? this.map.getZoom() : 7;
        const bearing = this.map && this.map.getBearing() ? this.map.getBearing() : 0;
        const pitch = this.map && this.map.getPitch() ? this.map.getPitch() : 0;

        this.initialCoordinates = {
            lat: value.latitude,
            lng: value.longitude,
            zoom: zoom,
            bearing: bearing,
            pitch: pitch,
        };

        if (this.map) {
            this.map.jumpTo([this.initialCoordinates.lng, this.initialCoordinates.lat]);
        }
    }

    /**
     * Called whenever the controller passes a new geofence to the map.
     *
     * If a valid new value is received, parse it to GeoJSON and update the map.
     * If the geofence is removed, remove it from MapboxGL Draw as well.
     *
     * @param newValue
     * @param oldValue
     */
    @Watch('geofence')
    onGeofenceChanged(newValue, oldValue) {
        if (newValue) {
            this.updateGeofence(this.geofence);
        } else if (!newValue && oldValue) {
            this.draw.deleteAll();
            this.temporaryFeatures = [];
            this.existingGeofence = null;
        }

        this.updateControls();
    }

    /**
     * Called whenever the Mapbox map is initialized and loaded.
     *
     * Used to set up the map layers and features.
     *
     * @param map
     */
    onMapboxLoaded(map) {
        this.map = map;

        this.map.addSource('geofence', {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: [],
            },
        });

        this.map.addSource('installation', {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: [
                    {
                        type: 'Feature',
                        geometry: {
                            type: 'Point',
                            coordinates: [this.coords.longitude, this.coords.latitude],
                        },
                    },
                ],
            },
        });

        let draw = new MapboxDraw({
            displayControlsDefault: false,
            // Select which mapbox-gl-draw control buttons to add to the map.
            modes: Object.assign(
                {
                    draw_rectangle: DrawRectangle,
                    draw_circle: DrawCircle,
                },
                MapboxDraw.modes,
            ),
            controls: {
                // No controls. We manually add control panel for drawing below.
            },
            // Set mapbox-gl-draw to draw by default.
            // The user does not have to click the polygon control button first.
            defaultMode: 'simple_select',
            styles: drawStyles,
        });

        this.draw = draw;
        this.map.addControl(new CustomDrawControls(draw), 'top-left');

        // We need to add draw to the map without controls, but the control container keeps some padding even if it's
        // empty. Add it to the bottom-left (where there are no other controls) to prevent this from visually showing.
        this.map.addControl(draw, 'bottom-left');

        this.map.on('draw.create', this.shapeCreated);
        this.map.on('draw.delete', this.shapeDeleted);
        this.map.on('draw.update', this.shapeCreated);

        if (this.existingGeofence && !this.initialized) {
            this.draw.add(this.existingGeofence);
        }

        // Add a Symbol layer for individual installation markers that are not in a cluster.
        this.map.addLayer(mapLayerIndividualInstallation);
        this.map.moveLayer('installation');

        this.updateControls();
    }

    /**
     * Called whenever a new MapboxGL Draw feature is created.
     *
     * Converts and emits the newly created shape back to the controller and updates the controls.
     *
     * @param e
     */
    shapeCreated(e) {
        if (e.features.length >= 1) {
            this.temporaryFeatures = [e.features[0]];
            this.$emit('geofence-changed', convertToOldGeofenceShape(e.features[0]));
        } else {
            this.temporaryFeatures = [];
            this.$emit('geofence-changed', null);
        }

        this.updateControls();
    }

    /**
     * Called whenever a MapboxGL Draw feature is deleted.
     *
     * Removes the feature, emits an update event and updates the controls.
     *
     * @param e
     */
    // eslint-disable-next-line no-unused-vars
    shapeDeleted(e) {
        this.deleted = true;
        this.temporaryFeatures = [];

        this.$emit('geofence-changed', null);
        this.updateControls();
    }

    /**
     * Update the drawing controls based on the current state of the map.
     *
     * If there is no existing or newly drawn shape yet:
     * - Show all draw controls, including the different shapes.
     *
     * If there is an existing shape:
     * - Hide the polygon, rectangle and circle drawing tools (or any other drawing tool that has the
     * `mapboxgl-ctrl-new-shape` class).
     * - Only show simple_select and trash buttons
     */
    updateControls() {
        const hasGeofenceShape =
            (this.existingGeofence != null &&
                this.temporaryFeatures.length === 0 &&
                !this.deleted) ||
            this.temporaryFeatures.length > 0;

        const controls = document.getElementsByClassName('mapboxgl-ctrl-new-shape');
        for (const index in controls) {
            let control = controls[index];

            if (control && control.style) {
                control.style.display = hasGeofenceShape ? 'none' : 'block';
            }
        }
    }

    updateGeofence(geofence) {
        const shape = parseOldGeofenceShape(geofence);
        if (shape) {
            this.existingGeofence = shape;
        }
    }
}
</script>
